import { doc, setDoc } from "firebase/firestore";
import React, { useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../firebase";
import { sendEmailVerification } from "firebase/auth";
import { createFreeSubscription } from "../../../utils/createFreeSubscription";
import CelebrationModal from "../../../components/CelebrationModal";

function DepartmentSignUp() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mob, setMob] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [error, setError] = useState("");
  const { signup, lawyerGoogleLogin } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationDays, setCelebrationDays] = useState(0);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const navigate = useNavigate();

  async function handleGoogleSignUp() {
    try {
      setError("");
      setIsAuthenticating(true);
      const result = await lawyerGoogleLogin("signup");

      if (result.success) {
        console.log(
          "Google sign-in successful, redirecting to:",
          result.redirectTo
        );
        navigate(result.redirectTo);
      }
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError(err.message || "Failed to sign in with Google");
    } finally {
      setIsAuthenticating(false);
    }
  }

  async function handleAuthenticate() {
    // Removed 'sync' - 'async' is the correct keyword
    setError("");

    // --- Input Validations (Keep as is) ---
    if (!name.trim()) {
      setError("Username is required");
      return;
    }
    // ... other validations ...
    if (!countryCode) {
      setError("Country code is required");
      return;
    }
    // --- End Validations ---

    if (isAuthenticating) {
      return;
    }

    try {
      setIsAuthenticating(true);
      const userCredential = await signup(email, password); // Assuming signup handles createUserWithEmailAndPassword

      console.log("User signed up successfully:", userCredential);
      const user = userCredential.user;

      localStorage.setItem("accessTokenGoogle", user.accessToken);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("userId", user.uid);
      localStorage.setItem("userEmail", user.email);

      // Send verification email IMMEDIATELY after signup
      // It's generally better to send it before saving profile data,
      // but sending after is also fine. Send before navigating.
      await sendEmailVerification(user);
      console.log("Verification email sent.");

      // Save user data to Firestore
      const userRef = doc(db, "lawyers", user.uid);
      await setDoc(
        userRef,
        {
          uid: user.uid,
          email: email,
          name: name,
          mobileWithCountryCode: countryCode + mob,
          mobileNumber: mob,
          countryCode: countryCode,
          createdAt: new Date(),
          experience: 0,
          type: "",
          address: "",
          profile: "",
          degree: "",
          lawyerId: "",
          location: "",
          firmName: "",
          privacy: "nnn",
          emailVerified: false, // Explicitly set initial verification status
        },
        { merge: true } // Merge is good practice here
      );
      console.log("User data saved to Firestore.");

      // Create the free subscription for the new user (duration comes from the
      // jradmin-controlled free plan config).
      const { created, durationDays } = await createFreeSubscription(user.uid);

      // Clear form fields
      setName("");
      setEmail("");
      setPassword("");
      setMob("");
      setCountryCode("");

      const navTarget = { path: "/portal/EmailVerification", state: { email: user.email } };
      if (created) {
        // Only celebrate when a free subscription was actually granted. Mark the
        // Dashboard first-time welcome as seen so it doesn't show the same popup again.
        localStorage.setItem("hasSeenWelcomePopup", "false");
        setCelebrationDays(durationDays);
        setPendingNavigation(navTarget);
        setShowCelebration(true);
      } else {
        navigate(navTarget.path, { state: navTarget.state });
      }
    } catch (err) {
      console.error("Authentication or Firestore error:", err);
      // --- Error Handling (Keep improved version) ---
      if (err.code === "auth/email-already-in-use") {
        setError("Email already in use. Try logging in.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address format.");
      } else if (err.code === "auth/weak-password") {
        setError("Password should be at least 6 characters.");
      } else if (err.code === "auth/operation-not-allowed") {
        setError("Email/Password sign-in is not enabled in Firebase.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many requests. Please try again later.");
      } else if (err.code === "auth/invalid-phone-number") {
        // Note: Firebase auth itself doesn't usually throw this on email signup
        // This might be from a custom validation or a different part of your code
        setError("Invalid phone number format.");
      } else {
        setError(err.message || "An unexpected error occurred during sign up.");
      }
      // --- End Error Handling ---
    } finally {
      setIsAuthenticating(false);
    }
  }

  return (
    <div className="min-h-screen overflow-hidden py-10 sm:py-0 flex flex-col bg-[#060223] items-center justify-center relative">
      <img
        src="../ellipse-top.png"
        className="absolute rotate-27 right-[-25vw] top-[-20vh] sm:right-[-150px] sm:top-[-200px] "
        alt=""
      />
      <img
        src="../ellipse-bottom.png"
        className="absolute sm:bottom-[-300px] bottom-[-20vh]"
        alt=""
      />
      <img
        src="../JURIDENT.png"
        className="absolute opacity-0 sm:opacity-[1] left-5"
        alt=""
      />
      <img
        src="../Jurident-Logo-bottom.png"
        className="absolute opacity-0 sm:opacity-[1] right-5 bottom-5"
        alt=""
      />
      <div className="sm:grid sm:grid-cols-3 gap-10 pt-14 items-center justify-center text-black p-4 z-10">
        <div className="flex flex-col items-center justify-center">
          <img src="../LawyerImage.png" alt="Lawyer" className="" />
        </div>

        <div className="w-full col-span-2 max-w-xs p-5 ">
          <h2 className="text-3xl font-semibold text-[#C99F4A] text-center">
           Department Sign Up
          </h2>
          {/* <p className="text-center text-gray-300 mt-2">
            Are you ready to become a legal eagle? Login to the app and spread
            your wings in the courtroom.
          </p>

          <h3 className="text-lg font-medium text-[#C99F4A] text-center mt-4">
            Join as Lawyer
          </h3> */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mt-4 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-center gap-4 mt-4">
            <button
              onClick={handleGoogleSignUp}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-black rounded-lg w-36"
              disabled={isAuthenticating}
            >
              {isAuthenticating ? (
                "Processing..."
              ) : (
                <>
                  <i className="fa-brands fa-google"></i> Google
                </>
              )}
            </button>
          </div>

          <div className="flex items-center my-4">
            <div className="flex-1 border-t border-gray-600"></div>
            <p className="px-3 text-gray-400">Or</p>
            <div className="flex-1 border-t border-gray-600"></div>
          </div>

          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              className="w-full p-3 bg-white text-black rounded-lg focus:outline-none"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full p-2.5 bg-white text-black rounded-lg focus:outline-none text-sm"
            />
            <div className="relative">
              <input
                id="password"
                value={password}
                type="password"
                placeholder="Password"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2.5 bg-white text-black rounded-lg focus:outline-none text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  let eye = document.getElementById("password-eye");
                  let togglepassword = document.getElementById("password");
                  if (togglepassword.type === "password") {
                    togglepassword.type = "text";
                    eye.classList.remove("fa-eye");
                    eye.classList.add("fa-eye-slash");
                  } else {
                    togglepassword.type = "password";
                    eye.classList.add("fa-eye");
                    eye.classList.remove("fa-eye-slash");
                  }
                }}
                className="absolute right-4 top-3 text-gray-400 cursor-pointer"
              >
                <i id="password-eye" className="fa-solid fa-eye"></i>
              </button>
            </div>
            <div className="flex items-center p-1 bg-white text-black rounded-lg justify-between gap-1">
              <div className="flex items-center p-1 text-black rounded-lg border-2 w-100%">
                <PhoneInput
                  international
                  defaultCountry="IN"
                  value={countryCode}
                  onChange={setCountryCode}
                  placeholder="Country Code"
                  className="w-24 pl-2 focus:outline-none"
                />
              </div>
              <div className="flex items-center p-1 text-black rounded-lg border-2">
                <input
                  type="tel"
                  value={mob}
                  onChange={(e) => setMob(e.target.value)}
                  placeholder="Phone Number"
                  className="w-full pl-2 focus:outline-none"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleAuthenticate}
              className="w-full bg-blue-600 p-2.5 rounded-lg text-white font-medium hover:bg-blue-700 text-sm"
              disabled={isAuthenticating}
            >
              {isAuthenticating ? "Processing..." : "Sign Up"}
            </button>
          </form>

          <p className="text-center text-gray-400 mt-4">
            Do you have an account?{" "}
            <span className="text-[#C99F4A] cursor-pointer">
              <Link to="../portal/ClientSignIn">Sign In</Link>
            </span>
          </p>
        </div>
      </div>

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={showCelebration}
        onClose={() => {
          setShowCelebration(false);
          if (pendingNavigation) {
            navigate(pendingNavigation.path, { state: pendingNavigation.state });
          }
        }}
        durationDays={celebrationDays}
      />
    </div>
  );
}

export default DepartmentSignUp;
